'use client';

import Link from 'next/link';
import Image from 'next/image';
import { useCallback, useMemo, useRef } from 'react';

import { useTranslations } from '@/components/language-provider';
import { useTheme } from '@/components/theme-provider';
import { VenuesNearMeSection } from '@/components/venues-near-me';
import { buildCourtIntelligenceShareMailto, COURT_INTELLIGENCE_FOUNDING_VENUES_PATH } from '@/lib/court-intelligence-marketing';
import { trackEvent } from '@/lib/analytics';

function CalendarIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true" {...props}>
      <path d="M8 2v3" />
      <path d="M16 2v3" />
      <path d="M3 9h18" />
      <path d="M5 5h14a2 2 0 0 1 2 2v11a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V7a2 2 0 0 1 2-2Z" />
      <path d="M7 13h4" />
      <path d="M7 17h7" />
    </svg>
  );
}

function SparklesIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true" {...props}>
      <path d="M12 3v2" />
      <path d="M5.22 6.22 6.64 7.64" />
      <path d="M3 12h2" />
      <path d="M5.22 17.78 6.64 16.36" />
      <path d="M12 19v2" />
      <path d="M17.36 16.36 18.78 17.78" />
      <path d="M19 12h2" />
      <path d="M17.36 7.64 18.78 6.22" />
      <path d="M8 12a4 4 0 1 0 8 0 4 4 0 0 0-8 0Z" />
    </svg>
  );
}

function ChartIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true" {...props}>
      <path d="M3 3v18h18" />
      <path d="M7 16l3-3 4 4 6-6" />
      <path d="M7 10h.01" />
      <path d="M11 6h.01" />
      <path d="M17 8h.01" />
    </svg>
  );
}

const featureIcons = [CalendarIcon, SparklesIcon, ChartIcon];
const APP_STORE_URL = 'https://apps.apple.com/us/app/whoplays-us-self-pickleball/id6451442623?platform=iphone';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.whosplays.app';

export function MarketingHome() {
  const { messages } = useTranslations();
  const { actualTheme } = useTheme();

  const features = useMemo(
    () =>
      messages.features.cards.map((card, index) => {
        const Icon = featureIcons[index] ?? featureIcons[0];
        return { ...card, Icon };
      }),
    [messages.features.cards],
  );
  const socialLinks = messages.social?.links ?? [];
  const finalCta = messages.finalCta;
  const courtIntelPromo = messages.homeCourtIntelPromo;
  const playerAdvocacy = messages.courtIntelligence.playerAdvocacy;
  const courtIntelLogoSrc = actualTheme === 'dark' ? '/images/court-intelligence-logo-dark.svg' : '/images/court-intelligence-logo.svg';
  const playerAdvocacyHref = useMemo(
    () => buildCourtIntelligenceShareMailto(playerAdvocacy.shareSubject, playerAdvocacy.shareBody),
    [playerAdvocacy.shareBody, playerAdvocacy.shareSubject],
  );
  const ownerPageHref = `${COURT_INTELLIGENCE_FOUNDING_VENUES_PATH}?utm_source=whoplays_home&utm_medium=player_advocacy_card&utm_campaign=owner_demand`;
  const playerAdvocacyVideoTrackedRef = useRef(false);

  const handlePlayerAdvocacyOwnerClick = useCallback(() => {
    trackEvent('home_player_advocacy_owner_page_click', {
      placement: 'player_advocacy_card',
    });
  }, []);

  const handlePlayerAdvocacyEmailClick = useCallback(() => {
    trackEvent('home_player_advocacy_email_click', {
      placement: 'player_advocacy_card',
    });
  }, []);

  const handlePlayerAdvocacyVideoPlay = useCallback(() => {
    if (playerAdvocacyVideoTrackedRef.current) return;
    playerAdvocacyVideoTrackedRef.current = true;
    trackEvent('home_player_advocacy_video_play', {
      placement: 'player_advocacy_card',
      duration_seconds: 50,
    });
  }, []);

  return (
    <div className="space-y-24 lg:space-y-32">
      <section className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)] lg:items-start">
        <div className="space-y-8">
          <span className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-muted/40 px-4 py-2 text-sm font-medium text-muted-foreground shadow-sm backdrop-blur">
            <span className="size-2 rounded-full bg-primary" aria-hidden="true" />
            {messages.hero.eyebrow}
          </span>
          <h1 className="text-balance text-4xl font-bold tracking-tight text-foreground sm:text-5xl lg:text-6xl">
            {messages.hero.title}
          </h1>
          <p className="max-w-xl text-lg text-muted-foreground sm:text-xl">{messages.hero.description}</p>
          <div className="flex flex-wrap items-center gap-3">
            <Link
              href={messages.hero.primaryHref}
              className="btn-primary inline-flex items-center justify-center rounded-full bg-primary px-6 py-3 text-base font-semibold shadow-lg shadow-primary/30 transition hover:-translate-y-0.5 hover:bg-primary/90 hover:shadow-xl focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
            >
              {messages.hero.primaryCta}
            </Link>
          </div>
        </div>
        <div>
          <div className="relative overflow-hidden rounded-3xl border border-border/60 bg-surface p-4 shadow-2xl shadow-primary/10">
            <div className="absolute -inset-12 bg-gradient-to-br from-primary/10 via-transparent to-primary/40" aria-hidden="true" />
            <div className="relative mx-auto w-full max-w-[620px]">
              <Image
                src="/images/whoplays-main-image2.webp"
                alt="WhoPlays mobile screens showing court feed and scheduling"
                width={720}
                height={560}
                priority
                className="h-auto max-h-[420px] w-full object-contain"
              />
            </div>
            <div className="relative mt-4 border-t border-border/40 pt-3">
              <p className="text-[10px] font-medium uppercase tracking-[0.14em] text-muted-foreground/60">{messages.hero.badgesLabel}</p>
              <div className="mt-2 flex flex-wrap items-center gap-2 sm:gap-3">
                <Link
                  href={APP_STORE_URL}
                  className="inline-flex h-12 w-36 items-center justify-center rounded-xl border border-border/70 bg-background/60 backdrop-blur"
                  aria-label="Download on the App Store"
                >
                  <Image
                    src="/images/apple-store-badge.svg"
                    alt="Download on the App Store"
                    width={179}
                    height={60}
                    className="block h-[33px] w-auto"
                  />
                </Link>
                <Link
                  href={PLAY_STORE_URL}
                  className="inline-flex h-12 w-36 items-center justify-center rounded-xl border border-border/70 bg-background/60 backdrop-blur"
                  aria-label="Get it on Google Play"
                >
                  <Image
                    src="/images/google-play-badge.png"
                    alt="Get it on Google Play"
                    width={646}
                    height={250}
                    className="block h-[41px] w-auto scale-[1.06]"
                  />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="-mt-8 grid gap-6 rounded-3xl border border-border/60 bg-surface p-6 shadow-lg shadow-primary/10 sm:p-8 lg:-mt-10 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)] lg:items-center">
        <div className="space-y-4">
          <span className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-muted/40 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            <span className="size-2 rounded-full bg-primary" aria-hidden="true" />
            {courtIntelPromo.badge}
          </span>
          <h2 className="text-balance text-2xl font-semibold text-foreground sm:text-3xl">{courtIntelPromo.title}</h2>
          <p className="text-sm text-muted-foreground sm:text-base">{courtIntelPromo.description}</p>
          <div className="flex flex-wrap gap-2">
            {courtIntelPromo.kpis.map((kpi) => (
              <span
                key={kpi}
                className="rounded-full border border-primary/40 bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-primary"
              >
                {kpi}
              </span>
            ))}
          </div>
          <Link
            href={courtIntelPromo.foundingHref}
            className="inline-flex items-center gap-2 text-sm font-semibold text-primary underline-offset-4 transition hover:underline"
          >
            <span className="size-2 rounded-full bg-primary/70" aria-hidden="true" />
            {courtIntelPromo.foundingNote}
          </Link>
        </div>
        <div className="space-y-4 rounded-2xl border border-primary/25 bg-gradient-to-br from-primary/20 to-primary/5 p-6">
          <Image
            src={courtIntelLogoSrc}
            alt="Court Intelligence logo"
            width={1200}
            height={320}
            className="h-auto w-full max-w-[420px]"
          />
          <div className="flex flex-wrap items-center gap-4 pt-1">
            <Link
              href={courtIntelPromo.primaryHref}
              className="inline-flex w-full items-center justify-center rounded-full bg-primary px-5 py-3 text-sm font-semibold btn-primary shadow-md shadow-primary/30 transition hover:-translate-y-0.5 hover:bg-primary/90 hover:shadow-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring sm:w-auto"
            >
              {courtIntelPromo.primaryCta}
            </Link>
            <Link
              href={courtIntelPromo.secondaryHref}
              className="inline-flex items-center justify-center rounded-full border border-border/70 bg-background/80 px-5 py-3 text-sm font-semibold text-foreground shadow-sm transition hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
            >
              {courtIntelPromo.secondaryCta}
            </Link>
          </div>
        </div>
      </section>

      <section className="grid gap-6 rounded-3xl border border-border/60 bg-surface p-6 shadow-lg shadow-primary/10 sm:p-8 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)] lg:items-start">
        <div className="flex h-full flex-col gap-4">
          <span className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-muted/40 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            <span className="size-2 rounded-full bg-primary" aria-hidden="true" />
            {playerAdvocacy.title}
          </span>
          <p className="text-base text-muted-foreground sm:text-lg">{playerAdvocacy.description}</p>
          <ul className="space-y-3 text-sm text-muted-foreground">
            {playerAdvocacy.points.map((point) => (
              <li key={point} className="flex items-start gap-3 rounded-2xl border border-border/50 bg-background px-4 py-3">
                <span className="mt-2 size-2 flex-shrink-0 rounded-full bg-primary" aria-hidden="true" />
                <span>{point}</span>
              </li>
            ))}
          </ul>
          <div className="mt-auto rounded-2xl border border-border/50 bg-background px-4 py-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">How to use this</p>
            <ol className="mt-2 space-y-1 text-sm text-muted-foreground">
              <li>1. Open the owner page.</li>
              <li>2. Share the 50-second demo video.</li>
              <li>3. Ask your venue manager to start setup.</li>
            </ol>
          </div>
        </div>
        <div className="space-y-4 rounded-2xl border border-primary/25 bg-gradient-to-br from-primary/15 to-background p-6 lg:w-full lg:max-w-[460px] lg:justify-self-end">
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">50-second demo</p>
            <div className="mx-auto w-full max-w-[420px] overflow-hidden rounded-xl border border-border/60 bg-black shadow-sm">
              <video
                className="h-auto w-full"
                controls
                playsInline
                preload="none"
                poster="/images/court-intelligence-og.jpg"
                onPlay={handlePlayerAdvocacyVideoPlay}
              >
                <source src="/videos/court-intelligence-players-720p.webm" type="video/webm" />
                <source src="/videos/court-intelligence-players-720p.mp4" type="video/mp4" />
              </video>
            </div>
          </div>
          <h2 className="text-2xl font-semibold text-foreground sm:text-3xl">{playerAdvocacy.title}</h2>
          <p className="text-sm text-muted-foreground">{playerAdvocacy.description}</p>
          <div className="flex flex-wrap gap-3 pt-1">
            <Link
              href={ownerPageHref}
              onClick={handlePlayerAdvocacyOwnerClick}
              className="inline-flex items-center justify-center rounded-full bg-primary px-5 py-2.5 text-sm font-semibold btn-primary shadow-md shadow-primary/30 transition hover:-translate-y-0.5 hover:bg-primary/90 hover:shadow-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
            >
              {playerAdvocacy.secondaryCta}
            </Link>
            <Link
              href={playerAdvocacyHref}
              onClick={handlePlayerAdvocacyEmailClick}
              className="inline-flex items-center justify-center rounded-full border border-border px-5 py-2.5 text-sm font-semibold text-foreground shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
            >
              {playerAdvocacy.primaryCta}
            </Link>
          </div>
        </div>
      </section>

      <section className="grid gap-6 rounded-3xl border border-border/60 bg-surface p-6 shadow-lg shadow-primary/5 sm:grid-cols-3 sm:p-8">
        {messages.stats.map((item) => (
          <div key={item.value} className="space-y-2 border-border/40 sm:border-r last:border-0 sm:pr-6 sm:last:pr-0">
            <p className="text-3xl font-bold text-foreground sm:text-4xl">{item.value}</p>
            <p className="text-sm text-muted-foreground sm:text-base">{item.label}</p>
          </div>
        ))}
      </section>

      <VenuesNearMeSection messages={messages.homeVenues} />

      <section id="features" className="space-y-12">
        <div className="space-y-4 text-center">
          <h2 className="text-balance text-3xl font-bold tracking-tight sm:text-4xl">{messages.features.title}</h2>
          <p className="mx-auto max-w-2xl text-lg text-muted-foreground">{messages.features.description}</p>
        </div>
        <div className="grid gap-6 lg:grid-cols-3">
          {features.map((feature) => (
            <div key={feature.title} className="flex h-full flex-col gap-4 rounded-2xl border border-border/60 bg-surface p-6 shadow-md shadow-primary/10">
              <div className="inline-flex size-11 items-center justify-center rounded-xl bg-primary/15 text-primary">
                <feature.Icon className="size-5" />
              </div>
              <div className="space-y-3">
                <h3 className="text-xl font-semibold text-foreground">{feature.title}</h3>
                <p className="text-sm text-muted-foreground">{feature.description}</p>
              </div>
              <ul className="mt-auto space-y-2 text-sm text-muted-foreground">
                {feature.points.map((point) => (
                  <li key={point} className="flex items-start gap-2">
                    <span className="mt-1 size-1.5 rounded-full bg-primary" aria-hidden="true" />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      <section className="grid gap-10 rounded-3xl border border-border/60 bg-surface/80 p-10 shadow-xl shadow-primary/10 backdrop-blur lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]">
        <div className="space-y-4">
          <h2 className="text-3xl font-bold text-foreground sm:text-4xl">{messages.workflow.title}</h2>
          <p className="text-lg text-muted-foreground">{messages.workflow.description}</p>
          <div className="space-y-6">
            {messages.workflow.steps.map((step, index) => (
              <div key={step.title} className="flex gap-4 rounded-2xl border border-border/50 bg-surface p-4 shadow-sm">
                <span className="flex size-10 items-center justify-center rounded-xl bg-primary/15 text-base font-semibold text-primary">
                  {index + 1}
                </span>
                <div className="space-y-1">
                  <h3 className="text-lg font-semibold text-foreground">{step.title}</h3>
                  <p className="text-sm text-muted-foreground">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="relative overflow-hidden rounded-3xl border border-border/60 bg-surface p-6 shadow-lg shadow-primary/10">
          <div className="absolute -inset-10 rounded-full bg-primary/10 blur-3xl" aria-hidden="true" />
          <div className="relative space-y-4">
            <h3 className="text-lg font-semibold text-foreground">{messages.workflow.title}</h3>
            <p className="text-sm text-muted-foreground">{messages.workflow.description}</p>
            <ul className="space-y-3 text-sm text-muted-foreground">
              {messages.workflow.steps.map((step) => (
                <li key={step.title} className="flex items-start gap-2">
                  <span className="mt-1 size-1.5 rounded-full bg-primary" aria-hidden="true" />
                  <span>{step.description}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="space-y-10 rounded-3xl border border-border/60 bg-surface px-6 py-10 shadow-lg shadow-primary/5 sm:px-10">
        <div className="max-w-3xl space-y-3">
          <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">{messages.features.title}</h2>
          <p className="text-base text-muted-foreground sm:text-lg">{messages.features.description}</p>
        </div>
        <div className="grid gap-6 md:grid-cols-3">
          {features.map((feature) => (
            <article key={feature.title} className="flex h-full flex-col gap-4 rounded-2xl border border-border/60 bg-background p-6 shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg">
              <feature.Icon className="size-10 text-primary" aria-hidden="true" />
              <h3 className="text-xl font-semibold text-foreground">{feature.title}</h3>
              <p className="text-sm text-muted-foreground">{feature.description}</p>
              <ul className="mt-auto space-y-2 text-sm text-muted-foreground">
                {feature.points.map((point) => (
                  <li key={point} className="flex items-start gap-2">
                    <span aria-hidden className="mt-1 size-1.5 rounded-full bg-primary" />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>

      {socialLinks.length > 0 ? (
        <section className="space-y-6 rounded-3xl border border-dashed border-border/60 bg-muted/30 px-6 py-10 shadow-inner sm:px-10">
          <div className="max-w-2xl space-y-3">
            <h2 className="text-2xl font-bold tracking-tight text-foreground">{messages.social.title}</h2>
            <p className="text-sm text-muted-foreground sm:text-base">{messages.social.description}</p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {socialLinks.map((link) => (
              <Link
                key={link.name}
                href={link.href}
                target="_blank"
                rel="noreferrer"
                className="group flex items-center justify-between rounded-2xl border border-border/60 bg-background px-5 py-4 text-sm font-semibold text-foreground shadow-sm transition hover:-translate-y-0.5 hover:bg-primary/5 hover:shadow-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
              >
                <span className="flex flex-col">
                  <span className="text-base font-semibold text-foreground">{link.name}</span>
                  <span className="text-xs text-muted-foreground">{link.handle}</span>
                </span>
                <span className="inline-flex size-10 items-center justify-center rounded-full border border-border/50 text-xs font-bold text-primary transition group-hover:border-primary group-hover:text-primary">
                  {link.name.charAt(0)}
                </span>
              </Link>
            ))}
          </div>
        </section>
      ) : null}

      <section className="space-y-6 rounded-3xl border border-border/60 bg-surface px-6 py-10 text-center shadow-lg shadow-primary/10 sm:px-10">
        <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">{finalCta.title}</h2>
        <p className="mx-auto max-w-3xl text-sm text-muted-foreground sm:text-base">{finalCta.description}</p>
        <div className="flex flex-wrap justify-center gap-3">
          <Link
            href={finalCta.primaryHref}
            className="inline-flex items-center justify-center rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground shadow transition hover:-translate-y-0.5 hover:bg-primary/90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
          >
            {finalCta.primaryCta}
          </Link>
          <Link
            href={finalCta.secondaryHref}
            className="inline-flex items-center justify-center rounded-full border border-border px-6 py-3 text-sm font-semibold text-foreground shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
          >
            {finalCta.secondaryCta}
          </Link>
        </div>
      </section>
    </div>
  );
}
